import { motion, AnimatePresence } from 'motion/react';
import { Check, Loader2, Satellite, Cloud, Database, Calculator, BarChart3, TrendingUp, Zap } from 'lucide-react';
import { SolarData } from '../types';

interface CalculationProgressModalProps {
  isOpen: boolean;
  calculationStep?: 'fetching' | 'processing' | 'calculating' | 'complete';
  solarData: SolarData | null;
  location: { latitude: number; longitude: number };
}

export function CalculationProgressModal({ 
  isOpen, 
  calculationStep = 'fetching', 
  solarData,
  location 
}: CalculationProgressModalProps) {
  if (!isOpen) return null;

  const steps = [
    {
      id: 'fetching',
      title: 'Fetching NASA POWER Satellite Data',
      description: 'Retrieving 4-year climate averages (2020-2023)',
      detail: 'Global Horizontal Irradiance • Temperature • Precipitation • Humidity',
      icon: Satellite,
      color: 'text-blue-600',
      bgColor: 'bg-blue-500',
    },
    {
      id: 'processing',
      title: 'Processing Solar & Climate Data',
      description: 'Calculating monthly energy production patterns',
      detail: 'System efficiency • Temperature coefficients • Soiling factors • Degradation rates',
      icon: Database,
      color: 'text-amber-600',
      bgColor: 'bg-amber-500',
    },
    {
      id: 'calculating',
      title: 'Running Financial Models',
      description: 'Computing 25-year investment metrics',
      detail: 'NPV • IRR • LCOE • Payback period • Cash flow projections',
      icon: Calculator,
      color: 'text-green-600',
      bgColor: 'bg-green-500',
    },
    {
      id: 'complete',
      title: 'Generating Comprehensive Report',
      description: 'Finalizing analysis with environmental impact',
      detail: 'Charts • Tables • Executive summary • ROI breakdown',
      icon: BarChart3,
      color: 'text-purple-600',
      bgColor: 'bg-purple-500',
    },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === calculationStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden"
        >
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-[#4F819F] via-[#5A8FAD] to-[#3d6580] px-8 py-6 text-white relative overflow-hidden">
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            <div className="relative flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Loader2 className="w-7 h-7 text-white animate-spin" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-1">Creating Your Analysis</h2>
                <p className="text-blue-100 text-sm flex items-center gap-2">
                  <Satellite className="w-4 h-4" />
                  Processing real-time NASA POWER satellite data
                </p>
              </div>
            </div>
            
            {/* Location info badge */}
            <div className="relative bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 inline-block">
              <p className="text-xs text-blue-100">
                📍 Location: <span className="font-semibold text-white">{location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°</span>
              </p>
            </div>
            
            {/* Progress bar */}
            <div className="relative mt-5">
              <div className="flex items-center justify-between text-xs text-blue-100 mb-2">
                <span className="font-medium">Overall Progress</span>
                <span className="font-bold text-white">{Math.round(progress)}%</span>
              </div>
              <div className="h-2.5 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                  className="h-full bg-gradient-to-r from-white via-green-200 to-green-400 rounded-full shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Content - Steps */}
          <div className="p-8 space-y-3 max-h-[60vh] overflow-y-auto">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const isPending = index > currentStepIndex;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`
                    relative p-5 rounded-xl border-2 transition-all
                    ${isCurrent ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300 shadow-lg' : ''}
                    ${isCompleted ? 'bg-green-50 border-green-300 shadow-sm' : ''}
                    ${isPending ? 'bg-slate-50 border-slate-200' : ''}
                  `}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div
                      className={`
                        flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all
                        ${isCurrent ? `${step.bgColor} shadow-lg` : ''}
                        ${isCompleted ? 'bg-green-500 shadow-md' : ''}
                        ${isPending ? 'bg-slate-300' : ''}
                      `}
                    >
                      {isCompleted ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 200 }}
                        >
                          <Check className="w-6 h-6 text-white" />
                        </motion.div>
                      ) : isCurrent ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        >
                          <StepIcon className="w-6 h-6 text-white" />
                        </motion.div>
                      ) : (
                        <StepIcon className="w-6 h-6 text-white" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h3 className={`
                          font-bold text-base
                          ${isCompleted ? 'text-green-700' : isCurrent ? 'text-slate-900' : 'text-slate-400'}
                        `}>
                          {step.title}
                        </h3>
                        
                        {/* Status badges */}
                        {isCompleted && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-xs font-semibold text-green-700 bg-green-100 px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Done
                          </motion.span>
                        )}
                        {isCurrent && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className={`text-xs font-semibold ${step.color} bg-blue-100 px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1.5`}
                          >
                            <motion.div
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 1, repeat: Infinity }}
                            >
                              <Zap className="w-3 h-3 fill-current" />
                            </motion.div>
                            Processing...
                          </motion.span>
                        )}
                      </div>
                      
                      <p className={`
                        text-sm mb-2
                        ${isCurrent ? 'text-slate-700 font-medium' : isCompleted ? 'text-green-600' : 'text-slate-400'}
                      `}>
                        {step.description}
                      </p>
                      
                      {/* Detail - show when active or complete */}
                      {(isCurrent || isCompleted) && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          transition={{ duration: 0.3 }}
                          className={`
                            text-xs font-mono px-3 py-2 rounded-lg mt-2
                            ${isCurrent ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}
                          `}
                        >
                          {step.detail}
                        </motion.div>
                      )}
                      
                      {/* Real data preview for NASA step */}
                      {step.id === 'fetching' && isCompleted && solarData && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 grid grid-cols-2 gap-2 text-xs"
                        >
                          <div className="bg-white border border-green-200 rounded-lg px-3 py-2">
                            <div className="text-slate-500 mb-0.5">Avg. Temperature</div>
                            <div className="font-bold text-green-700">{solarData.avgTemperature.toFixed(1)}°C</div>
                          </div>
                          <div className="bg-white border border-green-200 rounded-lg px-3 py-2">
                            <div className="text-slate-500 mb-0.5">Annual GHI</div>
                            <div className="font-bold text-green-700">{solarData.annualGHI.toFixed(0)} kWh/m²</div>
                          </div>
                          <div className="bg-white border border-green-200 rounded-lg px-3 py-2">
                            <div className="text-slate-500 mb-0.5">Avg. Precipitation</div>
                            <div className="font-bold text-green-700">{solarData.avgPrecipitation.toFixed(1)} mm/day</div>
                          </div>
                          <div className="bg-white border border-green-200 rounded-lg px-3 py-2">
                            <div className="text-slate-500 mb-0.5">Avg. Humidity</div>
                            <div className="font-bold text-green-700">{solarData.avgHumidity.toFixed(0)}%</div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Footer with info */}
          <div className="px-8 pb-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 text-sm">
                <p className="font-semibold text-blue-900 mb-1">
                  Professional-Grade Analysis in Progress
                </p>
                <p className="text-blue-700 text-xs leading-relaxed">
                  Your analysis is being calculated using validated NASA satellite data, 
                  NREL PVWatts methodology, and IEC 61724 industry standards. 
                  This ensures the highest accuracy for your investment decision.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}